import { useState } from "react";
import { FiX } from "react-icons/fi";
import { ExerciseInPlan } from "../../../../types";
import { ImageModal } from "../../../conversations/ImageModal";
import { ExerciseSelect } from "./ExerciseSelect";
import utils from "../../../../helpers/utils";
import loadingImage from "../../../../assets/loading.png";

interface ExerciseRowProps {
  exercise: ExerciseInPlan;
  prescIndex: number;
  exerIndex: number;
  professionalId: string;
  onUpdate: (field: keyof ExerciseInPlan, value: string | number) => void;
  onRemove: () => void;
}

export const ExerciseRow = ({
  exercise,
  prescIndex,
  exerIndex,
  professionalId,
  onUpdate,
  onRemove,
}: ExerciseRowProps) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Prioriza image_url, depois image, e por último usa getUriImage com fallback
  let imageUrl: string;
  if (exercise.image_url && exercise.image_url.trim() !== "") {
    imageUrl = exercise.image_url;
  } else if (exercise.image && exercise.image.trim() !== "") {
    imageUrl = exercise.image;
  } else {
    imageUrl = utils.getUriImage(exercise?.image ?? loadingImage, "all_view");
  }

  return (
    <>
      <div className="group/exercise relative space-y-2.5 sm:space-y-3 md:space-y-0 md:grid md:grid-cols-12 md:gap-3 rounded-md sm:rounded-lg md:rounded-xl border border-stroke bg-gray-50/50 p-2.5 sm:p-3 md:p-4 transition-all md:hover:border-primary/30 md:hover:bg-white md:hover:shadow-md dark:border-strokedark dark:bg-meta-4/50 md:dark:hover:border-primary/30 md:dark:hover:bg-boxdark min-w-0 overflow-x-hidden">
        {/* Exercício - Mobile First */}
        <div className="md:col-span-3 flex items-end gap-2 overflow-visible">
          <div className="flex-1 min-w-0 overflow-visible">
            <label
              htmlFor={`exercise-${prescIndex}-${exerIndex}`}
              className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 md:hidden"
            >
              Exercício
            </label>
            <ExerciseSelect
              professionalId={professionalId}
              value={exercise.name || ""}
              onChange={(name, id, exerciseData) => {
                onUpdate("name", name);
                onUpdate("id", id);
                if (exerciseData) {
                  const imageValue =
                    exerciseData.image_url || exerciseData.image || "";
                  if (imageValue) {
                    onUpdate("image", imageValue);
                    onUpdate("image_url", imageValue);
                  }
                }
              }}
            />
          </div>
          {/* Botão Remover - Ao lado do select */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (
                globalThis.confirm(
                  "Tem certeza que deseja remover este exercício?"
                )
              ) {
                onRemove();
              }
            }}
            className="shrink-0 flex items-center justify-center rounded-md sm:rounded-lg border-2 border-red-200 p-1.5 sm:p-2 text-red-400 transition-all active:bg-red-50 active:text-red-600 active:border-red-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300 dark:border-red-800 dark:text-red-400 dark:active:bg-red-900/20 dark:hover:bg-red-900/20 dark:active:border-red-700 dark:hover:border-red-700 touch-manipulation"
            title="Remover exercício"
          >
            <FiX size={16} className="sm:w-[18px] sm:h-[18px] md:w-5 md:h-5" />
          </button>
        </div>

        {/* Linha de Inputs 1: Séries e Reps - Mobile First (2 por linha) */}
        <div className="grid grid-cols-2 gap-2 sm:gap-2.5 md:contents">
          {/* Séries - Mobile First */}
          <div className="md:col-span-2">
            <label
              htmlFor={`sets-${prescIndex}-${exerIndex}`}
              className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 md:hidden"
            >
              Séries
            </label>
            <input
              id={`sets-${prescIndex}-${exerIndex}`}
              type="text"
              value={exercise.sets || exercise.series || ""}
              onChange={(e) => onUpdate("sets", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="3"
              className="w-full box-border max-w-full rounded-md sm:rounded-lg border border-stroke bg-white px-2 sm:px-2.5 md:px-3 py-2 sm:py-2.5 text-center text-xs sm:text-sm font-semibold outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark dark:text-white touch-manipulation"
            />
          </div>

          {/* Reps - Mobile First */}
          <div className="md:col-span-2">
            <label
              htmlFor={`reps-${prescIndex}-${exerIndex}`}
              className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 md:hidden"
            >
              Repetições
            </label>
            <input
              id={`reps-${prescIndex}-${exerIndex}`}
              type="text"
              value={exercise.reps || exercise.repetitions || ""}
              onChange={(e) => onUpdate("reps", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="12"
              className="w-full box-border max-w-full rounded-md sm:rounded-lg border border-stroke bg-white px-2 sm:px-2.5 md:px-3 py-2 sm:py-2.5 text-center text-xs sm:text-sm font-semibold outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark dark:text-white touch-manipulation"
            />
          </div>
        </div>

        {/* Linha de Inputs 2: Carga e Intervalo - Mobile First (2 por linha) */}
        <div className="grid grid-cols-2 gap-2 sm:gap-2.5 md:contents">
          {/* Carga - Mobile First */}
          <div className="md:col-span-2">
            <label
              htmlFor={`load-${prescIndex}-${exerIndex}`}
              className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 md:hidden"
            >
              Carga
            </label>
            <input
              id={`load-${prescIndex}-${exerIndex}`}
              type="text"
              value={exercise.load || ""}
              onChange={(e) => onUpdate("load", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="20kg"
              className="w-full box-border max-w-full rounded-md sm:rounded-lg border border-stroke bg-white px-2 sm:px-2.5 md:px-3 py-2 sm:py-2.5 text-center text-xs sm:text-sm font-semibold outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark dark:text-white touch-manipulation"
            />
          </div>

          {/* Intervalo - Mobile First */}
          <div className="md:col-span-1">
            <label
              htmlFor={`interval-${prescIndex}-${exerIndex}`}
              className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 md:hidden"
            >
              Intervalo
            </label>
            <input
              id={`interval-${prescIndex}-${exerIndex}`}
              type="text"
              value={exercise.interval || ""}
              onChange={(e) => onUpdate("interval", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="60"
              className="w-full rounded-md sm:rounded-lg border border-stroke bg-white px-1.5 sm:px-2 py-2 sm:py-2.5 text-center text-xs sm:text-sm font-semibold outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark dark:text-white touch-manipulation"
            />
          </div>
        </div>

        {/* Linha de Inputs 3: Imagem - Mobile First */}
        <div className="md:contents">
          {/* Imagem - Mobile First (ocupa 2 colunas no mobile) */}
          <div className="md:col-span-1 flex items-center justify-center">
            {imageUrl ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImageModalOpen(true);
                }}
                className="h-20 sm:h-24 md:h-[42px] w-full rounded-md sm:rounded-lg border border-stroke bg-white overflow-hidden transition active:border-primary active:ring-2 active:ring-primary/20 md:hover:border-primary md:hover:ring-2 md:hover:ring-primary/20 dark:border-strokedark dark:bg-boxdark touch-manipulation"
                title="Clique para ver imagem em tamanho maior"
              >
                <img
                  src={imageUrl}
                  alt={exercise.name || "Exercício"}
                  className="h-full w-full object-cover"
                  style={{ objectFit: "contain" }}
                />
              </button>
            ) : (
              <div className="h-20 sm:h-24 md:h-[42px] w-full rounded-md sm:rounded-lg border border-stroke bg-gray-100 dark:border-strokedark dark:bg-meta-4 flex items-center justify-center">
                <span className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-600">
                  -
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Imagem */}
      {imageUrl && (
        <ImageModal
          imageUrl={exercise.image_url || exercise.image || ""}
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          alt={exercise.name || "Exercício"}
        />
      )}
    </>
  );
};
